import React, { useEffect } from 'react';
import { Controller, useFormContext, useFieldArray, useWatch } from 'react-hook-form';
import { Switch, Input, InputNumber, Divider, message } from 'antd';
import { Info, Plus, Trash2, Eye } from 'react-feather';
import { Typography, IconButton, Button, Tooltip, Link } from 'ui-kit';
import _ from 'lodash';
import './ProductPayoutSettings.scss';

export default function ProductPayoutSettings() {
  const { control, getValues, setValue } = useFormContext();

  const { product } = getValues();

  const isCustomPayoutSplit = useWatch({
    control,
    name: 'product.isCustomPayoutSplit',
  });

  const {
    fields: recipientAmountFields,
    append: appendAmountRecipient,
    remove: removeAmountRecipient,
  } = useFieldArray({
    control,
    name: 'product.amountSplits',
  });

  const {
    fields: recipientPercentageFields,
    append: appendPercentageRecipient,
    remove: removePercentageRecipient,
  } = useFieldArray({
    control,
    name: 'product.percentageSplits',
  });

  useEffect(() => {
    if (recipientAmountFields.length === 0) {
      appendAmountRecipient({ recipient: '', amount: '' });
    }
  }, []);

  const handleNewAmountRecipient = () => {
    appendAmountRecipient({
      recipient: '',
      amount: '',
    });
  };

  const handleNewPercentageRecipient = () => {
    appendPercentageRecipient({
      recipient: '',
      percents: '',
    });
  };

  const handleToggleChange = (targetField) => {
    const currentCustom = getValues('product.isCustomPayoutSplit') || false;
    const currentDefault = getValues('product.isDefaultPayoutSplit') ?? true;

    if (targetField === 'default') {
      setValue('product.isDefaultPayoutSplit', !currentDefault);
      setValue('product.isCustomPayoutSplit', currentDefault);
    } else {
      const nextCustom = !currentCustom;
      setValue('product.isCustomPayoutSplit', nextCustom);
      setValue('product.isDefaultPayoutSplit', !nextCustom);

      if (nextCustom) {
        const currentSplits = getValues('product.percentageSplits');
        if (!currentSplits || currentSplits.length === 0) {
          setValue('product.percentageSplits', [
            { recipient: 'Default store owner', percents: 100 },
          ]);
        }
      }
    }
  };

  const watchedAmountSplits = useWatch({
    control,
    name: 'product.amountSplits',
  });

  const watchedPercentageSplits = useWatch({
    control,
    name: 'product.percentageSplits',
  });

  const amountSplits =
    watchedAmountSplits?.length > 0 ? watchedAmountSplits : product?.amountSplits || [];

  const percentageSplits =
    watchedPercentageSplits?.length > 0 ? watchedPercentageSplits : product?.percentageSplits || [];

  const totalRecipientAmount = _.sumBy(
    amountSplits,
    (item: { amount?: string | number }) => Number(item?.amount) || 0,
  );

  const totalRecipientPercentage = _.sumBy(
    percentageSplits,
    (item: { percents?: string | number }) => Number(item?.percents) || 0,
  );

  const productPrice = Number(product?.price || 0);

  const isWholesaleCostValid = totalRecipientAmount <= productPrice;
  const isPercentageValid = totalRecipientPercentage === 100;

  const profitToSplit = Math.max(0, productPrice - totalRecipientAmount);

  const totalProfitAllocated = percentageSplits.reduce((acc, item) => {
    const percent = Number(item.percents || 0);
    return acc + (profitToSplit * percent) / 100;
  }, 0);

  const totalAllocated = totalRecipientAmount + totalProfitAllocated;

  useEffect(() => {
    if (isCustomPayoutSplit && totalRecipientPercentage > 100) {
      message.error(
        `Total profit split cannot exceed 100% (Currently ${totalRecipientPercentage}%).`,
        5,
      );
    }
  }, [isCustomPayoutSplit, totalRecipientPercentage]);

  return (
    <div className="payout-modal">
      <div className="payout-modal__content">
        <div className="payout-modal__toggle-box" style={{ marginBottom: 20 }}>
          <Controller
            control={control}
            name="product.isDefaultPayoutSplit"
            render={({ field: { value } }) => (
              <div
                className="toggle-group"
                style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}
              >
                <Switch checked={Boolean(value)} onChange={() => handleToggleChange('default')} />
                <div className="toggle-label-content">
                  <label
                    style={{ cursor: 'pointer', fontWeight: 500 }}
                    onClick={() => handleToggleChange('default')}
                  >
                    Use {'  '}
                    <Link
                      to="/overview/payouts/"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#047857', textDecoration: 'underline' }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      store default payout split
                    </Link>
                  </label>
                </div>
              </div>
            )}
          />
        </div>

        <div className="payout-modal__toggle-box" style={{ marginBottom: 20 }}>
          <Controller
            control={control}
            name="product.isCustomPayoutSplit"
            render={({ field: { value } }) => (
              <div
                className="toggle-group"
                style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}
              >
                <Switch checked={Boolean(value)} onChange={() => handleToggleChange('custom')} />
                <div className="toggle-label-content">
                  <label
                    style={{ cursor: 'pointer', fontWeight: 500 }}
                    onClick={() => handleToggleChange('custom')}
                  >
                    Set a custom split for this product only
                  </label>
                </div>
              </div>
            )}
          />
        </div>

        {isCustomPayoutSplit && (
          <div className="payout-modal__grid">
            <div className="payout-modal__form">
              <div className="payout-modal__banner">
                <Tooltip title="">
                  <IconButton style={{ padding: 0 }}>
                    <Info size={16} />
                  </IconButton>
                </Tooltip>
                <div className="banner-text">
                  <strong>This split is for reporting and tracking only.</strong>
                  <span>All payouts will go to the store&apos;s primary payout method.</span>
                </div>
              </div>

              <div className="payout-modal__section">
                <div className="section-header">
                  <div className="title-group">
                    <span className="badge">1</span>
                    <div>
                      <Typography variant="h6">Wholesale cost</Typography>
                      <p>
                        Enter any fixed costs (e.g., vendor or service provider) associated with
                        this product.
                      </p>
                    </div>
                  </div>

                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Plus size={14} />}
                    onClick={handleNewAmountRecipient}
                    className="add-btn"
                  >
                    Add recipient
                  </Button>
                </div>

                {recipientAmountFields.length > 0 && (
                  <div className="list-labels">
                    <span className="label-recipient">Recipient</span>
                    <span className="label-value">Amount</span>
                  </div>
                )}

                {!isWholesaleCostValid && (
                  <div style={{ color: '#ff4d4f', marginTop: 8, fontSize: 13, fontWeight: 500 }}>
                    Wholesale cost (${totalRecipientAmount.toFixed(2)}) cannot exceed product price
                    (${productPrice.toFixed(2)}).
                  </div>
                )}

                {recipientAmountFields.map((field, index) => (
                  <div key={field.id} style={{ marginBottom: 12 }}>
                    <div className="row-input">
                      <Controller
                        control={control}
                        name={`product.amountSplits.${index}.recipient`}
                        render={({
                          field: { ref: _ref, ...fieldProps },
                          fieldState: { error },
                        }) => (
                          <Input
                            {...fieldProps}
                            placeholder="e.g. Cleaner"
                            className={`text-input ${
                              !_.isUndefined(error) ? 'ant-input-status-error' : ''
                            }`}
                          />
                        )}
                      />

                      <div className="value-input-wrapper">
                        <span>$</span>
                        <Controller
                          control={control}
                          name={`product.amountSplits.${index}.amount`}
                          render={({
                            field: { ref: _ref, ...fieldProps },
                            fieldState: { error },
                          }) => (
                            <InputNumber
                              {...fieldProps}
                              placeholder="0"
                              bordered={false}
                              className={!_.isUndefined(error) ? 'ant-input-status-error' : ''}
                            />
                          )}
                        />
                      </div>

                      <button
                        type="button"
                        className="delete-btn"
                        onClick={() => removeAmountRecipient(index)}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>

                    <Controller
                      control={control}
                      name={`product.amountSplits.${index}.recipient`}
                      render={({ fieldState: { error } }) => (
                        <>
                          {!_.isUndefined(error) && (
                            <div style={{ color: '#ff4d4f', fontSize: 12, marginTop: 4 }}>
                              {error?.message}
                            </div>
                          )}
                        </>
                      )}
                    />
                    <Controller
                      control={control}
                      name={`product.amountSplits.${index}.amount`}
                      render={({ fieldState: { error } }) => (
                        <>
                          {!_.isUndefined(error) && (
                            <div style={{ color: '#ff4d4f', fontSize: 12, marginTop: 4 }}>
                              {error?.message}
                            </div>
                          )}
                        </>
                      )}
                    />
                  </div>
                ))}

                <div className="total-bar">
                  <div className="label-group">
                    <Typography variant="h6">Total wholesale cost</Typography>
                  </div>
                  <span
                    className={`total-value ${!isWholesaleCostValid ? 'error-text' : 'highlight'}`}
                    style={{ color: !isWholesaleCostValid ? '#ff4d4f' : undefined }}
                  >
                    ${totalRecipientAmount.toFixed(2)}
                  </span>
                </div>
              </div>

              <div className="payout-modal__section">
                <div className="section-header">
                  <div className="title-group">
                    <span className="badge">2</span>
                    <div>
                      <Typography variant="h6">Profit split</Typography>
                      <p>The remaining profit is split between recipients below.</p>
                    </div>
                  </div>

                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Plus size={14} />}
                    onClick={handleNewPercentageRecipient}
                    className="add-btn"
                  >
                    Add recipient
                  </Button>
                </div>

                {recipientPercentageFields.length > 0 && (
                  <div className="list-labels">
                    <span className="label-recipient">Recipient</span>
                    <span className="label-value">Percentage</span>
                  </div>
                )}

                {!isPercentageValid && (
                  <div
                    style={{
                      color: '#ff4d4f',
                      margin: '8px 0 12px 0',
                      fontSize: 13,
                      fontWeight: 500,
                    }}
                  >
                    Total profit split must equal 100% (Currently {totalRecipientPercentage}%).
                  </div>
                )}

                {recipientPercentageFields.map((field, index) => (
                  <div key={field.id} style={{ marginBottom: 12 }}>
                    <div className="row-input">
                      <Controller
                        control={control}
                        name={`product.percentageSplits.${index}.recipient`}
                        render={({
                          field: { ref: _ref, ...fieldProps },
                          fieldState: { error },
                        }) => (
                          <Input
                            {...fieldProps}
                            placeholder="Recipient"
                            className={`text-input ${
                              !_.isUndefined(error) ? 'ant-input-status-error' : ''
                            }`}
                          />
                        )}
                      />

                      <div className="value-input-wrapper">
                        <Controller
                          control={control}
                          name={`product.percentageSplits.${index}.percents`}
                          render={({
                            field: { ref: _ref, ...fieldProps },
                            fieldState: { error },
                          }) => (
                            <InputNumber
                              {...fieldProps}
                              placeholder="0"
                              bordered={false}
                              min={0}
                              max={100}
                              className={!_.isUndefined(error) ? 'ant-input-status-error' : ''}
                            />
                          )}
                        />
                        <span>%</span>
                      </div>

                      <button
                        type="button"
                        className="delete-btn"
                        onClick={() => removePercentageRecipient(index)}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>

                    <Controller
                      control={control}
                      name={`product.percentageSplits.${index}.recipient`}
                      render={({ fieldState: { error } }) => (
                        <>
                          {!_.isUndefined(error) && (
                            <div style={{ color: '#ff4d4f', fontSize: 12, marginTop: 4 }}>
                              {error?.message}
                            </div>
                          )}
                        </>
                      )}
                    />
                    <Controller
                      control={control}
                      name={`product.percentageSplits.${index}.percents`}
                      render={({ fieldState: { error } }) => (
                        <>
                          {!_.isUndefined(error) && (
                            <div style={{ color: '#ff4d4f', fontSize: 12, marginTop: 4 }}>
                              {error?.message}
                            </div>
                          )}
                        </>
                      )}
                    />
                  </div>
                ))}

                <Controller
                  control={control}
                  name="product.percentageSplits"
                  render={({ fieldState: { error } }) => {
                    const arrayError = error?.message || error?.root?.message;
                    return (
                      <>
                        {arrayError && (
                          <div
                            style={{
                              color: '#ff4d4f',
                              marginBottom: 12,
                              fontSize: 13,
                              fontWeight: 500,
                            }}
                          >
                            {String(arrayError)}
                          </div>
                        )}
                      </>
                    );
                  }}
                />

                <div className="total-bar">
                  <div className="label-group">
                    <Typography variant="h6">Total profit split</Typography>
                  </div>
                  <span
                    className={`total-value ${!isPercentageValid ? 'error-text' : 'highlight'}`}
                    style={{ color: !isPercentageValid ? '#ff4d4f' : undefined }}
                  >
                    {totalRecipientPercentage}%
                  </span>
                </div>
              </div>
            </div>

            {/* Right - Preview */}
            <div className="payout-modal__preview">
              <div>
                <div className="preview-header">
                  <Typography variant="h6">Total</Typography>
                  <Eye size={16} />
                </div>

                <div className="preview-row bold">
                  <Typography variant="body1" className="label">
                    Product price
                  </Typography>
                  <Typography variant="body2">${productPrice.toFixed(2)}</Typography>
                </div>

                <Divider style={{ margin: '15px 0' }} />

                <div className="preview-group">
                  <div className="preview-section-title">Wholesale cost</div>

                  {amountSplits.map((item, index) => (
                    <div key={index} className="preview-row">
                      <span className="label">{item.recipient || ''}</span>
                      <span className="value">${Number(item.amount || 0).toFixed(2)}</span>
                    </div>
                  ))}
                </div>

                <Divider style={{ margin: '15px 0' }} />

                <div className="preview-group">
                  <div className="preview-section-title">Profit split</div>

                  {percentageSplits.map((item, index) => {
                    const percent = Number(item.percents || 0);
                    const calculatedAmount = (profitToSplit * percent) / 100;

                    return (
                      <div key={index} className="preview-row">
                        <span className="label">
                          {item.recipient || ''} ({item.percents || 0}%)
                        </span>
                        <span className="value">${calculatedAmount.toFixed(2)}</span>
                      </div>
                    );
                  })}
                </div>

                <Divider style={{ margin: '15px 0' }} />

                <div className="preview-total-box">
                  <Typography variant="body1">Total allocated</Typography>
                  <span
                    className="amount"
                    style={{
                      color: !isPercentageValid || !isWholesaleCostValid ? '#ff4d4f' : undefined,
                    }}
                  >
                    ${totalAllocated.toFixed(2)}
                  </span>
                </div>
              </div>

              <div className="preview-footer-note">
                <Info size={14} style={{ flexShrink: 0, marginTop: 2 }} />
                <Typography variant="h6" style={{ fontSize: 11 }}>
                  For reporting and tracking only.
                  <br />
                  All payouts go to your primary payout method.
                </Typography>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
